/**
 * PNG download for an SVG chart.
 *
 * The charts are drawn in CSS tokens (`fill="var(--s1)"`, `font-size="var(--t-chart-tick)"`),
 * which an <img> rendering a serialised SVG cannot resolve — it has no stylesheet. So
 * the SVG is cloned and every element's fill, stroke, font and opacity are written in
 * as the computed values from the live document, then it is painted onto a canvas at
 * 2× with the page surface behind it and the chart's title above it.
 *
 * Web fonts are not embedded: the image falls back to the system's sans and mono, so
 * the export is legible in both themes but not pixel-identical to the screen.
 */
import type { ExportResult } from './export'

const STYLE_PROPS = [
  'fill',
  'stroke',
  'stroke-width',
  'stroke-dasharray',
  'stroke-linejoin',
  'stroke-linecap',
  'opacity',
  'font-family',
  'font-size',
  'font-weight',
  'text-anchor',
  'paint-order',
] as const

function inlineStyles(source: Element, target: Element) {
  const cs = getComputedStyle(source)
  const decl: string[] = []
  for (const p of STYLE_PROPS) {
    const v = cs.getPropertyValue(p)
    if (v) decl.push(`${p}:${v}`)
  }
  target.setAttribute('style', decl.join(';'))
  // Attribute values still holding a var() would override nothing useful; drop them.
  for (const a of ['fill', 'stroke', 'font-size', 'font-family']) {
    if (target.getAttribute(a)?.includes('var(')) target.removeAttribute(a)
  }
  const s = source.children
  const t = target.children
  for (let i = 0; i < s.length; i++) inlineStyles(s[i]!, t[i]!)
}

/** A legend entry as drawn under the chart: swatch colour and label. */
export interface LegendEntry {
  colour: string
  label: string
}

export function downloadSVGAsPNG(
  svg: SVGSVGElement,
  filename: string,
  opts: { title?: string; subtitle?: string; legend?: LegendEntry[] } = {},
): Promise<ExportResult> {
  return new Promise((resolve) => {
    try {
      const box = svg.getBoundingClientRect()
      const width = Math.round(box.width)
      const height = Math.round(box.height)
      const clone = svg.cloneNode(true) as SVGSVGElement
      inlineStyles(svg, clone)
      clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
      clone.setAttribute('width', String(width))
      clone.setAttribute('height', String(height))
      clone.style.overflow = 'visible'

      const root = getComputedStyle(document.documentElement)
      const surface = root.getPropertyValue('--surface').trim() || '#fff'
      const ink = root.getPropertyValue('--ink').trim() || '#000'
      const ink3 = root.getPropertyValue('--ink-3').trim() || '#777'

      const scale = 2
      const pad = 24
      const headH = (opts.title ? 30 : 0) + (opts.subtitle ? 18 : 0)
      const legendH = opts.legend?.length ? 26 : 0
      const canvas = document.createElement('canvas')
      canvas.width = (width + pad * 2) * scale
      canvas.height = (height + headH + legendH + pad * 2) * scale
      const ctx = canvas.getContext('2d')
      if (!ctx) return resolve('blocked')
      ctx.scale(scale, scale)
      ctx.fillStyle = surface
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      let y = pad
      if (opts.title) {
        ctx.fillStyle = ink
        ctx.font = '600 17px system-ui, sans-serif'
        ctx.fillText(opts.title, pad, y + 16)
        y += 30
      }
      if (opts.subtitle) {
        ctx.fillStyle = ink3
        ctx.font = '12px system-ui, sans-serif'
        ctx.fillText(opts.subtitle, pad, y + 12)
        y += 18
      }

      const blob = new Blob([new XMLSerializer().serializeToString(clone)], {
        type: 'image/svg+xml;charset=utf-8',
      })
      const url = URL.createObjectURL(blob)
      const img = new Image()
      img.onload = () => {
        ctx.drawImage(img, pad, y, width, height)
        URL.revokeObjectURL(url)
        if (opts.legend?.length) {
          let x = pad
          const ly = y + height + 14
          ctx.font = '11px system-ui, sans-serif'
          for (const e of opts.legend) {
            ctx.fillStyle = e.colour
            ctx.fillRect(x, ly - 8, 9, 9)
            ctx.fillStyle = ink3
            ctx.fillText(e.label, x + 14, ly)
            x += 14 + ctx.measureText(e.label).width + 18
          }
        }
        canvas.toBlob((png) => {
          if (!png) return resolve('blocked')
          const a = document.createElement('a')
          a.href = URL.createObjectURL(png)
          a.download = filename
          a.rel = 'noopener'
          document.body.appendChild(a)
          a.click()
          a.remove()
          setTimeout(() => URL.revokeObjectURL(a.href), 0)
          resolve('ok')
        }, 'image/png')
      }
      img.onerror = () => {
        URL.revokeObjectURL(url)
        resolve('blocked')
      }
      img.src = url
    } catch {
      resolve('blocked')
    }
  })
}
